import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useAnimationControls } from 'motion/react';
import { X, Gift, Package, Sparkles, Ticket, Star, Play, Check, AlertCircle, Crown, Lock, Loader2, ArrowLeft } from 'lucide-react';
import { UserData } from '../hooks/useUser';
import CurrencyIcon from './CurrencyIcon';
import { cn } from '../lib/utils';
import Confetti from './Confetti';
import { audioEngine } from '../utils/audioEngine';
import AnimaTicketIcon from './AnimaTicketIcon';

import { adManager } from '../utils/adManager';
import { DAILY_REWARD_TRACK, DailyReward, SingleReward, getRewardLabel } from '../utils/dailyRewards';
import Prism3D from './Prism3D';
import ChestIcon from './ChestIcon';
import { useTranslation } from 'react-i18next';

interface DailyGiftModalProps {
  user: UserData;
  onClose: () => void;
  onClaim: (track: 'basic' | 'premium', double?: boolean) => Promise<{ type: string; amount: number; isDouble: boolean } | null>;
  onUpgrade?: () => void;
}

// DAILY_REWARDS and PREMIUM_DAILY_REWARDS removed in favor of central definition

const traitColorMap: Record<string, string> = {
  red: "#f43f5e", yellow: "#fbbf24", green: "#10b981", blue: "#38bdf8", violet: "#a855f7"
};

const potentialColorMap: Record<string, string> = {
  gc_multi: "#fbbf24",
  gc_bonus: "#fbbf24",
  exp_bonus: "#10b981",
  reward_multi: "#f97316",
  hybrid_bonus: "#f97316",
  reward_flat: "#6366f1",
  score_bonus: "#a855f7",
  fever_bonus: "#f43f5e",
  time_bonus: "#38bdf8"
};

export default function DailyGiftModal({ user, onClose, onClaim, onUpgrade }: DailyGiftModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'idle' | 'pressing' | 'shaking' | 'revealing' | 'rewarded'>('idle');
  const [isClaiming, setIsClaiming] = useState(false);
  const [rewardData, setRewardData] = useState<{ type: string; amount: number; isDouble: boolean; rewards?: any[] } | null>(null);
  const [showDoubleButton, setShowDoubleButton] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState('');
  const controls = useAnimationControls();

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const nextMidnight = new Date();
      nextMidnight.setUTCHours(24, 0, 0, 0);
      
      const diff = nextMidnight.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft('00H 00M 00S');
        return;
      }
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(`${h.toString().padStart(2, '0')}H ${m.toString().padStart(2, '0')}M ${s.toString().padStart(2, '0')}S`);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  const isBasic = true;
  const currentStreak = user.dailyGift?.currentStreak || 0;
  
  const isClaimedToday = user.dailyGift?.isClaimedToday;

  const nextDay = (currentStreak % 28) + 1;
  const currentWeekIndex = Math.floor((nextDay - 1) / 7);
  const weeklyRewards = DAILY_REWARD_TRACK.slice(currentWeekIndex * 7, (currentWeekIndex + 1) * 7);


  const crateBase = "hsl(210, 20%, 30%)";
  const crateLidGold = "hsl(45, 100%, 50%)"; 
  const crateLidBronze = "hsl(30, 40%, 45%)";
  const crateLidSilver = "hsl(210, 10%, 75%)";
  const crateLidSpecial = "hsl(190, 90%, 65%)";
  
  const getLidColor = () => {
    const todayReward = DAILY_REWARD_TRACK[nextDay - 1];
    const freeReward = todayReward.free;
    const isBox = Array.isArray(freeReward) 
      ? freeReward.some(r => r.type === 'box') 
      : freeReward.type === 'box';
      
    if (isBox) {
      const box = Array.isArray(freeReward) 
        ? freeReward.find(r => r.type === 'box') 
        : freeReward;
        
      if (box?.subType === 'gold') return crateLidGold;
      if (box?.subType === 'silver') return crateLidSilver;
      if (box?.subType === 'diamond') return crateLidSpecial;
      return crateLidBronze;
    }
    if (nextDay % 7 === 0) return crateLidSpecial;
    return "hsl(210, 25%, 25%)"; 
  };

  const lidColor = getLidColor();



  const handleOpenCrate = async () => {
    if (step !== 'idle' && step !== 'pressing') return;
    setStep('pressing');
    await controls.start({ scale: 0.92, y: 12, transition: { duration: 0.1 } });
    setStep('shaking');
    await controls.start({
      rotate: [0, -8, 8, -10, 10, -8, 8, 0],
      x: [0, -4, 4, -4, 4, -4, 4, 0],
      scale: 1, y: 0, transition: { duration: 0.8 }
    });
    setStep('revealing');
    
    // Reveal what's inside (without claiming yet)
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const dayData = DAILY_REWARD_TRACK[nextDay - 1];
    const rawReward = dayData.free;
    const rewardsArray = Array.isArray(rawReward) ? rawReward : [rawReward];
    
    let finalRewardsArray = rewardsArray.map(r => ({ ...r, label: getRewardLabel(r) }));

    if (user.isPremium) {
      finalRewardsArray = [
        ...finalRewardsArray,
        ...finalRewardsArray.map(r => ({ ...r, isFromPro: true }))
      ];
    }
    
    setRewardData({
      type: rewardsArray.length > 1 ? 'Multi' : rewardsArray[0].type,
      amount: rewardsArray.length > 1 ? 0 : (rewardsArray[0] as any).amount,
      isDouble: user.isPremium,
      rewards: finalRewardsArray
    } as any);

    setStep('rewarded');
    audioEngine.playClaim();
  };

  const handleFinalClaim = async (double: boolean) => {
    if (isClaiming) return;
    setIsClaiming(true);
    setError(null);
    try {
      if (double && !user.isPremium) {
        const success = await adManager.showRewardedAd('daily_double');
        if (!success) {
          setError(t('dailyGift.ad_not_finished'));
          setIsClaiming(false);
          return;
        }
      }
      
      const result = await onClaim('basic', double);
      if (result) {
        audioEngine.playClaim();
        // Do not overwrite rewardData to preserve the custom PRO badges
        setShowDoubleButton(false);
        
        // If it was a normal claim, we can close or stay. 
        // Let's stay in 'rewarded' state but the buttons will change (handled by showDoubleButton)
      }
    } catch (err) {
      setError(t('dailyGift.connection_error'));
    } finally {
      setIsClaiming(false);
    }
  };


  const getRewardIcon = (reward: DailyReward, size: 'sm' | 'lg' = 'sm', prismInstance?: any) => {
    const r = Array.isArray(reward) ? reward[0] : reward;
    const iconSize = size === 'sm' ? "w-6 h-6" : "w-16 h-16";
    
    const wrapIcon = (content: React.ReactNode) => (
      <div className={cn(iconSize, "flex items-center justify-center relative")}>
        {content}
      </div>
    );

    if (r.type === 'prism') {
      return wrapIcon(<AnimaTicketIcon stars={r.stars || 1} size={size === 'sm' ? 'xs' : 'lg'} />);
    }
    if (r.type === 'coins') return wrapIcon(<CurrencyIcon type="coin" size={size === 'sm' ? 'md' : 'xl'} />);
    if (r.type === 'gems') return wrapIcon(<CurrencyIcon type="gem" size={size === 'sm' ? 'md' : 'xl'} />);
    if (r.type === 'tickets') {
      const ticketType = r.subType === 'star' ? "ticket_star" : "ticket_heart";
      return wrapIcon(<CurrencyIcon type={ticketType as any} size={size === 'sm' ? 'md' : 'xl'} />);
    }
    if (r.type === 'box') {
      return wrapIcon(<ChestIcon tier={r.subType as any} size={size === 'sm' ? 'xs' : 'lg'} />);
    }
    return wrapIcon(<Star className="w-[80%] h-[80%] text-amber-400 fill-current" />);
  };

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] text-slate-900 relative overflow-hidden font-num">
      <Confetti active={step === 'rewarded'} count={100} />
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#22d3ee 2px, transparent 2px)', backgroundSize: '32px 32px' }} />

      {/* Header - Leaderboard Style */}
      <div className="bg-white border-b border-slate-100 px-6 py-4 flex flex-col gap-3 relative z-10 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => {
                if (step === 'rewarded' && showDoubleButton) {
                  // If they are on the selection screen, let them close? 
                  // User says: "claim from the pop up screen". 
                  // If they close now, they lose the chance to claim today?
                  // Better to keep them here until they click something.
                  return;
                }
                audioEngine.playSound('click');
                onClose();
              }}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-slate-900" />
            </button>
            <div className="flex flex-col">
              <h2 className="text-3xl font-num font-black uppercase tracking-tight text-slate-900 leading-none">{t('dailyGift.title')}</h2>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-yellow-400/10 px-3 py-1.5 rounded-2xl border border-yellow-400/20">
              <CurrencyIcon type="coin" size="sm" />
              <span className="font-num text-sm font-black text-yellow-600">{user.coins?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Tab Switcher retired */}
      </div>

      <div className="flex-1 overflow-y-auto p-6 pb-24 relative z-10 scrollbar-none">
        <div className="max-w-[500px] mx-auto w-full">
          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-red-500 text-white p-4 rounded-2xl text-center text-[10px] font-black z-50 flex items-center justify-center gap-2 mb-6">
                <AlertCircle className="w-4 h-4" /> {error}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-col items-center">
          <AnimatePresence mode="wait">
            {(step === 'idle' || step === 'pressing' || step === 'shaking' || step === 'revealing') && (
              <motion.div 
                key="unbox-flow" 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.95 }} 
                className="w-full flex flex-col items-center"
              >
                {/* Current Day Banner */}
                <div className="w-full bg-white p-4 rounded-[24px] border-2 border-slate-100 flex items-center justify-between mb-4 shadow-sm relative overflow-hidden">
                   <div className="absolute top-0 right-0 p-4 opacity-5">
                      <Gift className="w-20 h-20 text-sky-500" />
                   </div>
                   <div className="relative z-10">
                      <div className="text-[10px] font-black text-sky-500 uppercase tracking-[0.2em] mb-0.5">{t('dailyGift.current_progress')}</div>
                      <div className="text-3xl font-num text-slate-900 leading-none">{t('dailyGift.day_of_28', { day: nextDay })}</div>
                      {!isClaimedToday && (
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                          {t('dailyGift.next_up')}: <span className="text-slate-900">{getRewardLabel(DAILY_REWARD_TRACK[nextDay - 1].free)}</span>
                        </div>
                      )}
                   </div>
                   
                   {!isClaimedToday && (
                     <div className="flex items-center gap-2 bg-sky-50 px-4 py-2 rounded-2xl border border-sky-100 shrink-0">
                        <Sparkles className="w-4 h-4 text-sky-500" />
                        <span className="text-[10px] font-black text-sky-600 uppercase tracking-widest leading-none">{t('dailyGift.ready')}</span>
                     </div>
                   )}
                </div>
                
                {/* 4x7 Calendar Grid */}
                <div className="w-full mb-4">
                  <div className="grid grid-cols-7 gap-2 bg-white p-3 rounded-[32px] border-2 border-slate-100 shadow-inner">
                    {DAILY_REWARD_TRACK.map((dayReward, i) => {
                      const absDay = i + 1;
                      const isCompleted = absDay <= currentStreak;
                      const isActuallyNext = absDay === currentStreak + 1;
                      
                      return (
                        <div key={i} className={cn(
                          "relative flex flex-col items-center justify-center aspect-[2/3] rounded-2xl transition-all border-2",
                          isCompleted ? "bg-emerald-50 border-emerald-100 opacity-60" : 
                          isActuallyNext ? "bg-white border-[#0ea5e9] shadow-[0_4px_0_#0ea5e9] scale-105 z-10" :
                          "bg-slate-50 border-slate-100 opacity-80"
                        )}>
                            <div className="flex flex-col items-center justify-between w-full h-full py-2">
                              <span className={cn(
                                "text-[11px] font-num font-black leading-none",
                                isActuallyNext ? "text-[#0ea5e9]" : 
                                isCompleted ? "text-emerald-500" : "text-slate-400"
                              )}>
                                {absDay}
                              </span>

                              {isCompleted ? (
                                <div className="flex-1 flex items-center justify-center">
                                  <Check className="w-5 h-5 text-emerald-500" />
                                </div>
                              ) : (
                                <>
                                  <div className="flex-1 flex items-center justify-center py-0.5">
                                    {getRewardIcon(dayReward.free, isActuallyNext ? 'sm' : 'sm')}
                                  </div>

                                  {(() => {
                                    const r = dayReward.free;
                                    const singleR = Array.isArray(r) ? r[0] : r;
                                    
                                    return (
                                      <div className="flex flex-col items-center h-3 justify-center">
                                        <span className="text-[9px] font-num font-black text-slate-900 whitespace-nowrap leading-none">
                                          {typeof singleR.amount === 'number' ? singleR.amount.toLocaleString() : singleR.amount}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="w-full space-y-4">
                  {!isClaimedToday ? (
                    <button 
                      onClick={handleOpenCrate} 
                      className="toy-3d-button border-none w-full bg-[#fbbf24] text-slate-900 font-num text-lg py-4 transition-all flex flex-col items-center gap-0.5"
                    >
                      {t('dailyGift.claim_day', { day: nextDay })}
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] opacity-60">
                         {getRewardLabel(DAILY_REWARD_TRACK[nextDay - 1].free)}
                      </span>
                    </button>
                  ) : (
                    <div className="text-center py-8 bg-white rounded-[32px] border-2 border-emerald-100 shadow-sm">
                      <div className="flex flex-col items-center justify-center gap-2 text-emerald-600">
                        <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-2">
                           <Check className="w-6 h-6" />
                        </div>
                        <span className="font-black uppercase tracking-[0.2em] text-sm">{t('dailyGift.all_secured')}</span>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 mb-2">{t('dailyGift.next_reward_tomorrow')}</p>
                        <div className="bg-emerald-100/50 px-4 py-2 rounded-xl border border-emerald-100">
                          <span className="font-num font-black text-emerald-600 text-lg tracking-widest">{timeLeft}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                </div>
              </motion.div>
            )}

            {step === 'rewarded' && rewardData && (
              <motion.div 
                key="rewarded" 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                className="flex flex-col items-center text-center w-full max-w-sm mx-auto"
              >
                <div className="bg-white p-8 rounded-[48px] border-2 border-slate-100 shadow-xl w-full flex flex-col items-center">
                  <div className="relative mb-8 mt-4 w-full space-y-8">
                    {/* Basic Rewards Group */}
                    <div className="flex flex-col items-center">
                      <div className="flex flex-wrap justify-center gap-6">
                        {(rewardData as any).rewards?.filter((r: any) => !r.isPremium).map((r: any, idx: number) => (
                          <motion.div 
                            key={idx}
                            initial={{ scale: 0, rotate: -10 }}
                            animate={{ scale: 1, rotate: 0 }}
                            className="flex flex-col items-center gap-2"
                          >
                            <div className="w-28 h-28 bg-slate-50 rounded-[32px] clay-base border-none shadow-md flex flex-col items-center justify-center relative z-10 p-3">
                              {/* Stars */}
                              {r.instances?.[0] && (
                                <div className="flex gap-1 mb-2">
                                  {Array.from({ length: r.instances[0].stars }).map((_, i) => (
                                    <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                  ))}
                                </div>
                              )}
                              
                              <div className="w-16 h-16">
                                {getRewardIcon(r, 'lg', (r as any).instances?.[0])}
                              </div>

                              {/* Stat Tray */}
                              {r.instances?.[0] && (
                                <div className="mt-3 w-full bg-white/80 backdrop-blur-sm rounded-xl p-1 flex items-center justify-center gap-1 border border-slate-200 shadow-inner">
                                  <div className="w-5 h-5 rounded-md flex items-center justify-center shadow-sm" style={{ backgroundColor: traitColorMap[r.instances[0].color] }}>
                                    <Star className="w-2.5 h-2.5 fill-white text-white" />
                                  </div>
                                  <div className="w-[1px] h-3 bg-slate-200 mx-0.5" />
                                  {[r.instances[0].slot1, r.instances[0].slot2, r.instances[0].slot3].filter(Boolean).map((pot, i) => (
                                    <div key={i} className="w-5 h-5 rounded-md flex items-center justify-center shadow-sm" style={{ backgroundColor: potentialColorMap[pot!.type] }} />
                                  ))}
                                </div>
                              )}

                              <div className="absolute -top-3 -right-3 bg-slate-900 text-white text-[11px] font-num font-black px-3.5 py-1.5 rounded-2xl shadow-xl border-2 border-white">
                                {r.amount}x
                              </div>
                              {r.isFromPro && (
                                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[9px] font-black px-2 py-0.5 rounded-xl uppercase tracking-widest border border-amber-400 shadow-sm whitespace-nowrap flex items-center gap-1">
                                  <Crown className="w-2.5 h-2.5 fill-white text-white" /> {t('dailyGift.pro_badge')}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                  </div>

                  <h2 className="text-3xl font-num text-slate-900 uppercase mb-2">
                    {showDoubleButton ? t('dailyGift.bonus_revealed') : t('dailyGift.claimed')}
                  </h2>
                  <div className="flex flex-wrap justify-center gap-2 mb-8">
                     {(rewardData as any).rewards?.map((r: any, idx: number) => (
                        <span key={idx} className="text-[9px] font-black text-[#0ea5e9] bg-[#0ea5e9]/5 px-3 py-1 rounded-full uppercase tracking-widest border border-[#0ea5e9]/10">
                          {r.label || `${r.amount} ${r.type}`}
                        </span>
                     ))}
                  </div>

                  <div className="w-full flex flex-col gap-3">
                    {showDoubleButton && (
                      <div className="flex flex-col gap-2">
                        {!user.isPremium && (
                          <button 
                            disabled={isClaiming} 
                            onClick={() => handleFinalClaim(true)} 
                            className="toy-3d-button border-none w-full bg-[#f43f5e] text-white font-num text-xl py-5 flex items-center justify-center gap-2"
                          >
                            {isClaiming ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <>
                                <Play className="w-5 h-5 fill-white" />
                                {t('dailyGift.double_up')}
                              </>
                            )}
                          </button>
                        )}
                        <button 
                          disabled={isClaiming} 
                          onClick={() => handleFinalClaim(user.isPremium)} 
                          className="toy-3d-button border-none w-full text-white font-num bg-emerald-500 text-lg py-3 flex items-center justify-center gap-2"
                        >
                          {isClaiming ? <Loader2 className="w-5 h-5 animate-spin" /> : t('dailyGift.receive')}
                        </button>
                      </div>
                    )}
                    {!showDoubleButton && (
                      <button 
                        onClick={() => {
                          audioEngine.playSound('click');
                          onClose();
                        }} 
                        className="toy-3d-button border-none w-full font-num text-xl py-5 transition-all bg-slate-900 text-white"
                      >
                        {t('dailyGift.close')}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
